import { Component, Input, ElementRef, NgZone, Output, EventEmitter, OnInit, OnChanges } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { NuclearObservable } from '../../nuclear-observable'
import { BaseComponent } from '../../components/base.component'
import { User, Account, BillingAccountWithBillingInfo, Country, State, RecurlyState } from '../../types'
import { geoGetters } from '../../stores/geo-store'
import { GeoActions } from '../../actions/geo-actions'
import { toUnderscoreString, decodeUrlParam } from '../../utils'
import repeat from 'lodash-es/repeat'
import sortBy from 'lodash-es/sortBy'
import get from 'lodash-es/get'
import pick from 'lodash-es/pick'
import { AccountActions } from '../../actions/account-actions'
import { SubscriptionActions } from '../../actions/subscription-actions'
import { successToast, errorToast, removeToasts } from '../../toaster'
import { environment } from '../../environments/environment'
import { FormControl } from '@angular/forms'
import { emailValid } from 'src/services/validation-service'

@Component({
  selector: 'payment-info-form',
  template: `
    <div class="container-fluid">
      <form id="paymentForm" (ngSubmit)="onSubmit()">
        <ng-template [ngIf]="currentUser && !(currentUser?.firstName && currentUser?.lastName)">
          <div>
            <div class="row small-padding-on-big-screens">
              <div class="col-sm-12">
                <h4 class="small-bottom-buffer">Your Contact Info</h4>
              </div>
            </div>
            <div class="row small-padding-on-big-screens">
              <div class="col-sm-6">
                <div class="form-group" [class.has-error]="angularError(userFirstName) || customError('userFirstName')">
                  <input type="text" name="userFirstName" #userFirstName="ngModel" [(ngModel)]="user.firstName" placeholder="First name" class="form-control" autofocus="true" (blur)="updateBillingFormSection('firstName', user.firstName)" required>
                  <div
                    *ngIf="angularError(userFirstName) || customError('userFirstName')"
                    class="error-text">
                    First name required
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="form-group" [class.has-error]="angularError(userLastName) || customError('userLastName')">
                  <input type="text" name="userLastName" #userLastName="ngModel" [(ngModel)]="user.lastName" placeholder="Last name" class="form-control" (blur)="updateBillingFormSection('lastName', user.lastName)" required>
                  <div
                    *ngIf="angularError(userLastName) || customError('userLastName')"
                    class="error-text">
                    Last name required
                  </div>
                </div>
              </div>
            </div>
            <div class="row small-padding-on-big-screens">
              <div class="col-sm-12">
                <div class="form-group" [class.has-error]="(billingEmail.invalid || !isEmailValid(billingEmail.value)) && (billingEmail.dirty || billingEmail.touched || submitted)">
                  <input type="email" name="email" #billingEmail="ngModel" [(ngModel)]="billing.email" placeholder="Email" class="form-control" required/>
                  <div *ngIf="(billingEmail.invalid || !isEmailValid(billingEmail.value)) && (billingEmail.dirty || billingEmail.touched || submitted)" class="error-text">
                    Email is invalid
                  </div>
                </div>
              </div>
            </div>
            <hr class="no-top-buffer bottom-buffer">
          </div>
        </ng-template>

        <div>
          <div class="row small-padding-on-big-screens">
            <div class="col-sm-12">
              <div [hidden]="!customError('message')" class="alert alert-danger">
                {{customError('message') | capitalize}}
              </div>
            </div>
          </div>
          <tabset [justified]="true">
            <tab id="ccTab" class="payment-tab" (selectTab)="updateStateSelectionFromBillingAccount()">
              <ng-template tabHeading>
                <i class="far fa-credit-card small-right-buffer"></i> <strong>Credit Card</strong>
              </ng-template>
              <div *ngIf="subscribedWithPayPal()" class="alert alert-danger row small-top-buffer small-padding-on-big-screens">
                <div class="col-sm-12">{{ paymentSwitchMessage }}</div>
              </div>
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-6">
                  <h4 class="my-2">Credit Card Info</h4>
                </div>
              </div>
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-6">
                  <div class="form-group" [class.has-error]="angularError(billingFirstName) || customError('first_name')">
                    <input data-recurly="first_name" type="text" name="firstName" #billingFirstName="ngModel" [(ngModel)]="billing.billingInfo.firstName" placeholder="First name" class="form-control" required>
                    <div *ngIf="angularError(billingFirstName) || customError('first_name')"
                      class="error-text">
                      First name required
                    </div>
                  </div>
                </div>
                <div class="col-sm-6">
                  <div class="form-group" [class.has-error]="angularError(billingLastName) || customError('last_name')">
                    <input data-recurly="last_name" type="text" name="lastName" #billingLastName="ngModel" [(ngModel)]="billing.billingInfo.lastName" placeholder="Last name" class="form-control" required>
                    <div *ngIf="angularError(billingLastName) || customError('last_name')"
                    class="error-text">
                      Last name required
                    </div>
                  </div>
                </div>
              </div>
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-12">
                  <div class="form-group">
                    <input data-recurly="company" type="text" name="company" [(ngModel)]="billing.billingInfo.company" placeholder="Company name" class="form-control" maxlength="50">
                  </div>
                </div>
              </div>

              <!--- BEGIN IFRAME BUSINESS --->
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-9">
                  <div class="form-group" [class.has-error]="cardErrors['number'] || customError('number')">
                    <div class="input-group w-100">
                      <div class="input-group-prepend w-100">
                        <span class="input-group-text" style="position: relative; right: -1px;">
                          <i class="fas fa-lock"></i>
                        </span>
                        <div data-recurly="number" id="number" name="number" class="w-100"></div>
                      </div>
                    </div>
                    <div [hidden]="!cardErrors['number'] && !customError('number')" class="tiny-top-buffer tiny-left-buffer text-danger small">
                      {{ cardErrors['number'] ? cardErrors['number'] : customError('number') }}
                    </div>
                  </div>
                </div>
                <div class="col-sm-3">
                  <div class="form-group" [class.has-error]="cardErrors['cvv'] || customError('cvv')">
                    <div data-recurly="cvv"></div>
                    <div [hidden]="!cardErrors['cvv'] && !customError('cvv')" class="tiny-top-buffer tiny-left-buffer text-danger small">
                      {{cardErrors['cvv'] ? cardErrors['cvv'] : customError('cvv')}}
                    </div>
                  </div>
                </div>
              </div>

              <div class="row small-padding-on-big-screens">
                <div class="col-sm-4 col-5">
                  <div class="form-group mb-0" [ngClass]="{'has-error': (!!cardErrors['date'] && (dirty['month'] || touched['month']) && dirty['month']) || !!customError('date')}">
                    <div data-recurly="month"></div>
                  </div>
                </div>
                <div class="col-sm-4 col-5">
                  <div class="form-group mb-0" [ngClass]="{'has-error': (!!cardErrors['date'] && (dirty['year'] || touched['year']) && dirty['year']) || !!customError('date')}">
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text py-0" style="position: relative; right: -1px;">20</span>
                        <div data-recurly="year"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div [hidden]="!cardErrors['date'] && !customError('date')" class="mt-1 ml-1 text-danger small">
                    {{cardErrors['date'] ? cardErrors['date'] : customError('date')}}
                  </div>
                </div>
              </div>
              <!--- END IFRAME BUSINESS --->

              <hr class="mt-3 mb-0"/>

              <div class="row small-padding-on-big-screens">
                <div class="col-sm-12">
                  <h4 class="my-2">Billing Address</h4>
                </div>
              </div>
              <!-- <div class="row small-padding-on-big-screens">
                <div class="col-sm-12">
                  <div class="form-group" [class.has-error]="emailError(billingEmail) || customError('email')">
                    <input type="email" name="email" #billingEmail="ngModel" [(ngModel)]="billing.email" placeholder="Email" class="form-control" required/>
                    <div *ngIf="emailError(billingEmail) || customError('email')"
                        class="tiny-top-buffer tiny-left-buffer text-danger small">
                      Email is invalid
                    </div>
                  </div>
                </div>
              </div> -->
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-8">
                  <div class="form-group" [class.has-error]="angularError(billingAddress) || customError('address1')">
                    <input data-recurly="address1" type="text" name="address1" #billingAddress="ngModel" [(ngModel)]="billing.billingInfo.address1" placeholder="Street Address" class="form-control" required/>
                    <div *ngIf="angularError(billingAddress) || customError('address1')"
                      class="error-text">
                      {{ customError('address1') || 'Billing address required' }}
                    </div>
                  </div>
                </div>
                <div class="col-sm-4">
                  <div class="form-group" [class.has-error]="customError('address2')">
                    <input data-recurly="address2" type="text" name="address2" [(ngModel)]="billing.billingInfo.address2" placeholder="Apt/Suite" class="form-control"/>
                    <div *ngIf="customError('address2')"
                      class="error-text">
                      {{ customError('address2') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-12">
                  <div class="form-group" [class.has-error]="angularError(billingCity) || customError('city')">
                    <input data-recurly="city" type="text" name="city" #billingCity="ngModel" [(ngModel)]="billing.billingInfo.city" placeholder="City" class="form-control" required/>
                    <div *ngIf="angularError(billingCity) || customError('city')"
                      class="error-text">
                      City required
                    </div>
                  </div>
                </div>
              </div>
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-7">
                  <div class="form-group" [class.has-error]="angularError(billingState) || customError('state')">
                    <select data-recurly="state" name="state" #billingState="ngModel" [(ngModel)]="billing.billingInfo.state" class="form-control state-select" [disabled]="states && states.length == 0" [required]="states && states.length > 0">
                      <option value="" disabled>State/Province</option>
                      <option *ngFor="let state of states" [value]="state.id">{{state.name}}</option>
                    </select>
                    <div *ngIf="angularError(billingState) || customError('state')"
                      class="error-text">
                      State required
                    </div>
                  </div>
                </div>
                <div class="col-sm-5">
                  <div class="form-group" [class.has-error]="angularError(billingZip) || customError('postal_code') || customError('zip')">
                    <input data-recurly="postal_code" type="text" name="postal_code" #billingZip="ngModel" [(ngModel)]="billing.billingInfo.zip" placeholder="Zip/Postal Code" class="form-control" maxlength="10" required/>
                    <div *ngIf="angularError(billingZip) || customError('postal_code') || customError('zip')"
                          class="tiny-left-buffer tiny-top-buffer text-danger small">
                      <div *ngIf="customError('postal_code')">{{customError('postal_code')}}</div>
                      <div *ngIf="customError('zip')">{{customError('zip')}}</div>
                      <div *ngIf="!customError('postal_code') && !customError('zip')">Zip code required</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-12">
                  <div class="form-group" [class.has-error]="angularError(billingCountry) || customError('country')">
                    <select data-recurly="country" name="country" #billingCountry="ngModel" [(ngModel)]="billing.billingInfo.country" class="form-control country-select" (ngModelChange)="updateStateSelection($event)" required>
                      <option value="" disabled>Country</option>
                      <optgroup label="----------------------------------">
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="GB">United Kingdom</option>
                      </optgroup>
                      <optgroup label="----------------------------------">
                        <option *ngFor="let country of alphabetized(countries)" [value]="country.id">{{country.name}}</option>
                      </optgroup>
                    </select>
                    <div *ngIf="angularError(billingCountry) || customError('country')"
                      class="error-text">
                      Country required
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-group mt-2" *ngIf="showTos()">
                <label class="bigger-than-normal-text">
                  <input name="userTermsOfService" #userTermsOfService="ngModel" [(ngModel)]="user.termsOfService" class="cursor-pointer" type="checkbox" required />
                  I have read and agree to the <a target="_blank" href="/pages/tos/customer">Verblio Terms of Service</a>
                </label>
                <div *ngIf="angularError(userTermsOfService) || customError('userTermsOfService')" class="error-text">
                  {{ customError('userTermsOfService') || 'Terms of service must be accepted' }}
                </div>
              </div>
              <input type="hidden" name="recurly-token" data-recurly="token">
              <div class="row small-padding-on-big-screens">
                <div class="col-sm-12 bottom-buffer">
                  <div *ngIf="newCustomer && submitted" class="my-1">Please be patient... Setting up your account</div>
                  <div [ngClass]="{ 'float-left right-buffer': !newCustomer, 'd-flex flex-row justify-content-end': newCustomer }">
                    <button
                      type="submit"
                      class="btn-block-sm btn-block-xs"
                      [ngClass]="{
                        'hot-orange-button btn-lg': newCustomer,
                        'blue-button': !newCustomer
                      }"
                      [disabled]="submitted">
                      <span *ngIf="submitted">
                        <span>Saving {{savingDots}}</span>
                      </span>
                      <span *ngIf="!submitted && (newCustomer || activationParams.activate)">{{ activationParams.activate ? 'Activate Subscription' : 'Activate Account' }}</span>
                      <span *ngIf="!submitted && !newCustomer && !activationParams.activate">Save</span>
                    </button>
                  </div>
                </div>
              </div>
            </tab>
            <tab id="paypalTab" class="payment-tab" [active]="subscribedWithPayPal()">
              <ng-template tabHeading>
                <img src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png" alt="PayPal" height="20">
              </ng-template>
              <div class="row bottom-buffer">
                <div class="col-sm-12">
                  <div *ngIf="subscribedWithCreditCard()" class="alert alert-danger row small-top-buffer small-padding-on-big-screens">
                    <div class="col-sm-12">{{ paymentSwitchMessage }}</div>
                  </div>
                  <!-- <div class="row small-top-buffer small-padding-on-big-screens">
                    <div class="col-sm-12">
                      <div class="form-group" [class.has-error]="emailError(billingEmail) || customError('email')">
                        <label for="name">Email for receiving invoices</label>
                        <input type="text" name="email" #billingEmail="ngModel" [(ngModel)]="billing.email" placeholder="Email" class="form-control" required/>
                        <div *ngIf="emailError(billingEmail) || customError('email')" class="alert alert-danger">
                          Email is invalid
                        </div>
                      </div>
                    </div>
                  </div> -->
                  <div class="row small-padding-on-big-screens">
                    <div class="col-sm-12">
                      <h4 class="mt-3">PayPal</h4>
                      <p>Proceed to your PayPal account to accept the Recurring Billing Agreement.</p>
                      <p>Your invoices will reflect the terms outlined in your plan.</p>
                      <button type="button" class="paypal-btn btn top-buffer" (click)="payPalCheckout()" [disabled]="!userValid() || submitted">
                        <i class="mr-2 fab fa-paypal"></i>
                        Subscribe with <span class="paypal"><span class="pay">Pay</span><span class="pal">Pal</span></span>
                      </button>
                      <br>
                      <button *ngIf="submitted" type="button" class="small-top-buffer btn btn-default" (click)="submitted = false">Cancel PayPal</button>
                      <div *ngIf="customError('userFirstName') || customError('userLastName')" class="tiny-top-buffer small text-muted">
                        (Enter your name above to complete checkout)
                      </div>
                      <div class="checkbox checkbox-orange-bg top-buffer">
                        <label class="bigger-than-normal-text">
                          <input name="userTermsOfService" #userTermsOfService="ngModel" [(ngModel)]="user.termsOfService" class="cursor-pointer" type="checkbox" required />
                          I have read and agree to the <a target="_blank" href="/pages/tos/customer">Verblio Terms of Service</a>
                        </label>
                      </div>
                      <div *ngIf="angularError(userTermsOfService) || customError('userTermsOfService')" class="left-buffer text-danger">
                        {{ customError('userTermsOfService') || 'Terms of service must be accepted' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </tab>
          </tabset>
        </div>
      </form>
    </div>
  `,
})
export class PaymentInfoForm extends BaseComponent implements OnInit, OnChanges {
  @Input() currentUser: User;
  @Input() account: Account;
  @Input() billingAccount: BillingAccountWithBillingInfo;
  @Input() countries: Country[] = [];
  @Input() states: State[] = [];
  @Input() newCustomer: boolean = false;
  @Output() onSubmitSuccess: EventEmitter<any> = new EventEmitter();

  recurly: any;
  recurlyState: RecurlyState
  customErrors: any = {};
  cardErrors: any = {};
  user: UserFormSection = { firstName: null, lastName: null, termsOfService: false };
  billing: BillingAccountWithBillingInfo;
  touched: {number: boolean, cvv: boolean, month: boolean, year: boolean} = {number: false, cvv: false, month: false, year: false};
  dirty: {number: boolean, cvv: boolean, month: boolean, year: boolean} = {number: false, cvv: false, month: false, year: false};
  activationParams: ActivationParams = { activate: false, customerId: null, planCode: null, options: [], quantity: 0 };
  submitted: boolean = false;
  savingDots: string = '...';
  private recurlyApiKey: string = environment.RECURLY_JS_API_KEY;

  constructor(
    private geoActions: GeoActions,
    private elementRef: ElementRef,
    private accountActions: AccountActions,
    private subscriptionActions: SubscriptionActions,
    private router: Router,
    private zone: NgZone,
    private route: ActivatedRoute
  ) {
    super()
  }

  ngOnChanges(changes: any): void {
    this.resetForm()
    this.resetRecurly()
  }

  ngOnInit(): void {
    const routeObs = this.route.queryParams.subscribe(queryParams => {
      this.activationParams = {
        activate: decodeUrlParam(queryParams['activate']) === 'true',
        customerId: +decodeUrlParam(queryParams['customerId']),
        planCode: decodeUrlParam(queryParams['planCode']),
        options: decodeUrlParam(queryParams['options']) && decodeUrlParam(queryParams['options']).split(','),
        quantity: +decodeUrlParam(queryParams['quantity'])
      }
    })
    this.observers.push(routeObs)
  }

  subscribedWithPayPal(): boolean {
    return this.billingAccount && this.billingAccount.method === 'paypal'
  }

  subscribedWithCreditCard(): boolean {
    return this.billingAccount && this.billingAccount.method === 'credit_card'
  }

  signingUp(): boolean {
    return this.account && this.account.currentCustomersCount === 0
  }

  resetRecurly(): void {
    this.recurly = window['recurly']
    if (this.recurly && (document.getElementById('ccTab') || document.getElementById('paypalTab'))) {
      this.recurly.configure({
        publicKey: this.recurlyApiKey,
        required: ['number', 'cvv', 'month', 'year'],
        fraud: {
          kount: { dataCollector: true }
        },
        style: {
          all: {
            fontFamily: 'Lato',
            fontWeight: '400',
            fontSize: '16px',
            placeholder: {
              color: '#bbb',
            }
          },
          number: {
            placeholder: {
              content: this.numberPlaceholder()
            }
          },
          month: {
            placeholder: {
              content: 'MM'
            }
          },
          year: {
            placeholder: {
              content: 'YY'
            }
          },
          cvv: {
            placeholder: {
              content: 'CVV',
            }
          }
        }
      })
      this.recurly.on('change', this.recurlyChangeHandler.bind(this))
    } else if (!this.recurly) {
      errorToast("We are unable to process billing at this time, please try again later.")
    }
  }

  resetForm(): void {
    this.user.firstName = this.user.firstName || (this.currentUser && this.currentUser.firstName)
    this.user.lastName = this.user.lastName || (this.currentUser && this.currentUser.lastName)
    this.user.termsOfService = this.user.termsOfService || (this.currentUser && this.currentUser.acceptedTos)

    this.billing = this.billing || Object.assign({
      firstName: this.currentUser ? this.currentUser.firstName : null,
      lastName: this.currentUser ? this.currentUser.lastName : null,
      email: this.currentUser ? this.currentUser.email : null,
      companyName: null,
      billingInfo: {
        firstName: null,
        lastName: null,
        company: null,
        address1: null,
        address2: null,
        city: null,
        state: '',
        zip: null,
        country: 'US'
      },
      paypal: false
    }, this.billingAccount)
  }

  updateBillingFormSection(key: string, value: string): void {
    if (!this.billing.billingInfo[key]) this.billing.billingInfo[key] = value
  }


  // #################################################################################
  // ####                          CREDIT CARD SUBMISSION                         ####
  // #################################################################################

  onSubmit(): void {
    if (!this.submitted) {
      const form = { user: this.user, billing: Object.assign({}, this.billing, { method: 'credit_card' }) }
      const validOrErrorMessage = this.valid(form)
      if (validOrErrorMessage === true) {
        this.submitted = true
        this.triggerSavingDots(true)
        // Submit Recurly Token
        this.fetchRecurlyToken(this.elementRef.nativeElement.querySelector('#paymentForm')).then(token => {
          // massage data more for account
          this.createOrUpdateBillingAccount(form, token.id)
        }).catch(err => {
          // Handle Tokenization errors
          this.zone.run(() => {
            for (let index in err.fields) {
              let field = err.fields[index]
              this.customErrors[field] = [this.errorMessage(err, index)]
            }
            this.submitted = false
            this.triggerSavingDots(false)
          })
        })
      } else {
        if (Object.keys(this.customErrors).length > 1) {
          this.customErrors['message'] = ['The following errors were encountered when saving your information...']
        } else {
          this.customErrors['message'] = [validOrErrorMessage]
        }
      }
    }
  }

  logRecurlyStatus() {
    console.groupCollapsed('recurlyState')
    console.error(this.recurlyState)
    console.error('payment error', this.customErrors['message'] || 'success')
    console.groupEnd()
  }

  onSuccess(): void {
    this.zone.run(() => {
      this.submitted = false
      this.triggerSavingDots(false)
      this.customErrors = {}
      this.logRecurlyStatus()
      this.onSubmitSuccess.emit(true)
    })
  }

  onError(res): void {
    this.zone.run(() => {
      this.submitted = false
      this.triggerSavingDots(false)
      this.customErrors = res.body ? res.body.errors : null
      // General invalid billing messages
      if (res.body && res.body.message) {
        this.customErrors = this.customErrors || {}
        this.customErrors['message'] = [res.body.message]
        this.logRecurlyStatus()
      }
    })
  }

  createOrUpdateBillingAccount(form: PaymentForm, token: string): void {
    // back end receives different structure than what's directly in the form
    const preparedForm = {
      id: this.account.id,
      token,
      method: form.billing.method,
      user: form.user,
      email: form.billing.email,
      firstName: form.billing.billingInfo && form.billing.billingInfo.firstName,
      lastName: form.billing.billingInfo && form.billing.billingInfo.lastName,
      companyName: form.billing.billingInfo && form.billing.billingInfo.company,
      address: pick(form.billing.billingInfo, ['address1', 'address2', 'city', 'state', 'zip', 'country']),
      billingInfo: undefined // don't send this to our server!
    }

    // Updating a previous billing account
    if (this.billingAccount) {
      this.accountActions.updateBillingAccount(preparedForm).then(() => {
        if (!this.account.fullySetUp) {
          this.accountActions.activate().then(() => {
            this.onSuccess()
          }).catch(res => this.onError(res))
        // Reactivating expired account also had expired credit card
        // Create subscription and take them to queue
        } else if (this.activationParams.activate) {
          removeToasts()
          successToast('Payment information successfully updated')
          const customerId = this.activationParams.customerId
          const planCode = this.activationParams.planCode
          const options = this.activationParams.options
          const quantity = this.activationParams.quantity
          this.subscriptionActions.createSubscription(customerId, planCode, options, quantity).then(_ => {
            successToast('Subscription successfully activated')
            this.router.navigate(['/dashboard', customerId, 'queued'])
          })
        } else {
          this.onSuccess()
        }
      }).catch(res => {
        this.onError(res)
      })
    }

    // Creating a new billing account and activate subscription
    else {
      this.accountActions.createBillingAccount(preparedForm).then(() => {
        this.accountActions.activate().then(() => {
          this.onSuccess()
        }).catch(res => this.onError(res))
      }).catch(res => {
        this.onError(res)
      })
    }
  }

  fetchRecurlyToken(paymentForm: HTMLFormElement): Promise<Token> {
    return new Promise((resolve, reject) => {
      this.recurly.token(paymentForm, (error, token) => {
        if (error) {
          reject(error)
        } else {
          resolve(token)
        }
      })
    })
  }

  // #################################################################################
  // ####                            PAYPAL SUBMISSION                            ####
  // #################################################################################

  payPalCheckout(): void {
    if (!this.submitted) {
      if (!this.recurly || !this.recurly.PayPal) {
        // Problem loading recurly
        const errorToastTemplate = `
          <div>A connectivity issue occurred while connecting to our billing service.</div>
          <div>Please make sure you are connected to the internet, then reload the page and try again.</div>
        `
        errorToast(errorToastTemplate)
      } else {

        const humanizedPrice = (this.account.totalPendingValueWithDiscount / 100).toLocaleString('en-US', {style: 'currency', currency: 'USD'})
        const paypal = this.recurly.PayPal({
          display: { displayName: `Verblio Plan - ${humanizedPrice}` }
        })
        this.submitted = true
        if (this.userValid() && this.isEmailValid(this.billing.email)) { // Just validate user
          paypal.start()

          // cancel
          paypal.on('cancel', () => {
            // Canceling seems to call error instead
            this.submitted = false
          })

          // error
          paypal.on('error', error => {
            // const code = error.code;
            const message = error.cause.message || error.message
            errorToast(message)
            this.submitted = false
          })

          // success - token
          paypal.on('token', (token: Token) => {
            const form = { user: this.user, billing: { email: this.billing.email, method: <'paypal'>'paypal', billingInfo: null } } // cheating with this type
            this.createOrUpdateBillingAccount(form, token.id)
          })
        }
      }
    }
  }

  // #################################################################################
  // ####                  CUSTOM VALIDATION AND ERROR HANDLING                   ####
  // #################################################################################

  isEmailValid(email: string): boolean {
    return emailValid(email)
  }

  valid(form: PaymentForm): true | string {
    this.customErrors = {}
    let error = null

    if (!this.userValid()) {
      if ([null, ''].includes(form.user.firstName))
        this.customErrors['userFirstName'] = 'First name required'
      if ([null, ''].includes(form.user.lastName)) {
        this.customErrors['userLastName'] = 'Last name required'
      }
      if (!this.user.termsOfService) {
        this.customErrors['userTermsOfService'] = 'You must accept the terms of service'
      }
      return this.customErrors['userFirstName'] || this.customErrors['userLastName'] || this.customErrors['userTermsOfService']
    }

    if (!this.isEmailValid(form.billing.email)) {
      this.customErrors['email'] = 'Email is invalid'
      return "Email invalid"
    }

    // When card number has placeholder XXXX-XXXX-XXXX-last4
    if (this.billingAccount && this.billingAccount.billingInfo && this.billingAccount.billingInfo.maskedCc
    && this.recurly && this.recurly.hostedFields.state.number.empty) {
        this.customErrors['number'] = ['You must re-enter your credit card number']
        return 'Credit card needs to be re-entered'
    }

    if (form.billing.billingInfo) {
      let validatePresenceOf = ['firstName', 'lastName', 'address1', 'city', 'zip', 'country', ...(this.states.length === 0 ? [] : ['state'])]
      validatePresenceOf.forEach(key => {
        if (!form.billing.billingInfo[key]) {
          this.customErrors[toUnderscoreString(key)] = 'This field cannot be blank'
          error = `${key} cannot be blank`
        }
      })
    }

    this.updateCardErrors(this.recurlyState)
    if (this.recurlyState) {
      if (!this.recurlyState.fields.number.valid) {
        this.customErrors['number'] = "Your card number appears to be invalid, please check it and try again."
        return 'Card number invalid'
      }
      if (!this.recurlyState.fields.cvv.valid) {
        this.customErrors['cvv'] = "Your card's cvv appears to be invalid."
        return 'Card cvv invalid'
      }
      // Month and year are grouped on year valid check
      if (!this.recurlyState.fields.year.valid) {
        this.customErrors['date'] = "Please check your card's expiration date and try again."
        return 'Card expiration invalid'
      }
    } else {
      return 'Issue contacting recurly'
    }

    // If all else passed, it's valid
    return error || true
  }

  userValid(): boolean {
    if (this.currentUser)
      return ![null, ''].includes(this.user.firstName) && ![null, ''].includes(this.user.lastName) && this.user.termsOfService
    else
      return true
  }

  angularError(formControl: FormControl): string | false {
    if (
      (this.submitted || formControl.touched || formControl.dirty) &&
      formControl.invalid
    )
      return 'Required'
    return false
  }

  emailError(formControl: FormControl): string | false {
    if (
      !this.isEmailValid(formControl.value) &&
      (formControl.touched || this.submitted)
    ) {
      return 'Email is invalid'
    }
    return this.angularError(formControl)
  }

  customError(field: string): string {
    let message = ''
    let errors = this.customErrors ? this.customErrors[field] : []
    if (errors) {
      if (typeof errors === 'string') {
        message = errors
      } else {
        message = errors[0]
      }
    }
    return message
  }

  recurlyChangeHandler(state: RecurlyState): void {
    this.recurlyState = state
    this.updateCardTouched(this.recurlyState)
    this.updateCardDirty(this.recurlyState)
    this.updateCardErrors(this.recurlyState)
  }

  updateCardTouched(state: RecurlyState): void {
    this.touched['number'] = this.touched['number'] || this.fieldState(state, 'number', 'focus')
    this.touched['cvv'] = this.touched['cvv'] || this.fieldState(state, 'cvv', 'focus')
    this.touched['month'] = this.touched['month'] || this.fieldState(state, 'month', 'focus')
    this.touched['year'] = this.touched['year'] || this.fieldState(state, 'year', 'focus')
  }

  updateCardDirty(state: RecurlyState): void {
    this.dirty['number'] = this.dirty['number'] || (this.touched['number'] && !this.fieldState(state, 'number', 'focus'))
    this.dirty['cvv'] = this.dirty['cvv'] || (this.touched['cvv'] && !this.fieldState(state, 'cvv', 'focus'))
    this.dirty['month'] = this.dirty['month'] || (this.touched['month'] && !this.fieldState(state, 'month', 'focus'))
    this.dirty['year'] = this.dirty['year'] || (this.touched['year'] && !this.fieldState(state, 'year', 'focus'))
  }

  updateCardErrors(state: RecurlyState): void {
    delete this.customErrors['number'] // Clear error for XXXX-XXXX... in case they tried to submit
    this.cardErrors = {}
    this.cardErrors['number'] = (this.dirty['number'] || this.submitted) && !this.fieldState(state, 'number', 'valid') ? 'is not a valid card number' : ''
    this.cardErrors['cvv'] = (this.dirty['cvv'] || this.submitted) && !this.fieldState(state, 'cvv', 'valid') ? 'not valid' : ''
    this.cardErrors['date'] = (this.dirty['month'] && this.dirty['year'] || this.submitted) &&
                              (!this.fieldState(state, 'month', 'valid') || !this.fieldState(state, 'year', 'valid')) ? 'not valid' : ''
  }

  fieldState(state: any, field: string, subfield: string): any {
    const defaults = { valid: false, focus: false, empty: true, length: 0 }
    // Recurly submission returns different type, i.e. {cvv: {dirty: true, etc...}} instead of {fields: {cvv: {dirty: true, etc...}}} on change
    const onSubmissionField = get(state, [field, subfield], false)
    return onSubmissionField ? onSubmissionField : get(state, ['fields', field, subfield], defaults[subfield])
  }

  errorMessage(err: any, index: number|string): string {
    let message = err.message
    message = message.split(/and |, and |, /)[index].trim()
    const arr = message.split('[')
    if (arr.length > 1) {
      return arr[0] + arr[1].substring(1, arr[1].length - 2)
    }
    else return message
  }

  // #################################################################################
  // ####     UPDATING STATE SELECTION WHEN A DIFFERENT COUNTRY IS SELECTED       ####
  // #################################################################################

  updateStateSelectionFromBillingAccount(): void {
    const countryCode = this.billingAccount?.billingInfo?.country || 'US'
    this.updateStateSelection(countryCode)
  }

  // Hits the server for the subdivisions of the selected country and sets it to 'states'
  updateStateSelection(countryId: string): void {
    this.geoActions.loadStatesForCountry(countryId).then(() => {
      this.states = NuclearObservable.currentValue<State[]>(geoGetters.states(countryId), { toJS: true })
      this.billing.billingInfo.state = ''
    })
  }


  // #################################################################################
  // ####                            NGFOR ARRAYS                                 ####
  // #################################################################################

  alphabetized(countries: Country[]): Country[] {
    return sortBy(countries, 'name')
  }

  // #################################################################################
  // ####                               UI ONLY                                   ####
  // #################################################################################

  triggerSavingDots(isTriggered: boolean): void {
    let interval
    if (isTriggered) {
      let numDots = this.savingDots.length
      let numNoDots
      interval = setInterval(() => {
        numDots = ((numDots) % 3) + 1
        this.savingDots = repeat(".", numDots)
        numNoDots = 3 - numDots
        this.savingDots += repeat('', numNoDots)
      }, 500)
    } else {
      clearInterval(interval)
    }
  }

  numberPlaceholder(): string {
    return get(this.billingAccount, ['billingInfo', 'maskedCc']) || 'Credit card number'
  }

  showTos(): boolean {
    return !this.account.fullySetUp
  }

  paymentSwitchMessage = `
    Notice: Switching your payment method from PayPal to credit card will cancel and restart all of your subscriptions.
    No pricing or plan details will be affected, though you will receive one or more emails confirming your switched subscriptions.
  `
}

export interface Token {
  id: string;
}

interface UserFormSection {
  firstName: string;
  lastName: string;
  termsOfService: boolean;
}

interface PaypalBilling {
  email: string;
  method: 'paypal';
  billingInfo: null;
}

interface PaymentForm {
  user: UserFormSection;
  billing: BillingAccountWithBillingInfo | PaypalBilling;
}

interface ActivationParams {
  activate: boolean;
  customerId: number;
  planCode: string;
  options: string[];
  quantity: number;
}
